import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import styles from '../../../styles/components/Modal/Open-Deals/AddOrderModal.module.css';
import CloseIcon from "@mui/icons-material/Close";
import { InputSelector } from '../../UI/InputSelector';
import { DropdownSelector } from '../../UI/DropdownSelector';
import { Notification } from '../../UI/Notifications';
import { handleError } from '../../../utils/custom/errorHandler';
import { createOrder } from '../../../utils/api/Lead/OpenDeals/createOrderApi';
import { getAllAssetsApi } from '../../../utils/api/Lead/OpenDeals/getAllAssetsApi';
import { getAssetByName } from '../../../utils/api/Lead/OpenDeals/getAssetByNameApi';
import { SearchableDropdown } from '../../UI/SearchableDropdown';
import { LangContext } from '../../../context/LangContext';
import { ThemeContext } from '../../../context/ThemeContext';

const translations = {
    ru: {
        title: "Добавить сделку",
        type: "Тип сделки",
        asset: "Актив",
        leverage: "Плечо",
        openPrice: "Цена открытия",
        volume: "Объем",
        takeProfit: "Цена Take Profit",
        stopLoss: "Цена Stop Loss",
        margin: "Маржа",
        confirm: "Подтвердить",
        creating: "Создание..."
    },
    en: {
        title: "Add Deal",
        type: "Deal Type",
        asset: "Asset",
        leverage: "Leverage",
        openPrice: "Open Price",
        volume: "Volume",
        takeProfit: "Take Profit Price",
        stopLoss: "Stop Loss Price",
        margin: "Margin",
        confirm: "Confirm",
        creating: "Creating..."
    },

};

export const AddOrderModal = ({ leadId, onClose, onOrderCreated }) => {
    const modalRef = useRef(null);
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const currentTranslation = translations[langSelected] || translations.ru;

    const [formData, setFormData] = useState({
        type: '',
        assets_id: '',
        leverage: '',
        open_price: '',
        volume: '',
        take_profit_price: '',
        stop_loss_price: '',
        margin: '',
    });
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [loading, setLoading] = useState(false);
    const [assetsOptions, setAssetsOptions] = useState([]);
    const [allAssetsData, setAllAssetsData] = useState([]);

    const fetchAllAssets = useCallback(async () => {
        try {
            const assets = await getAllAssetsApi();
            setAllAssetsData(assets);
            const options = assets.map(asset => ({
                value: asset.platform_id,
                label: asset.name,
            })).filter(option => option.value !== null);
            setAssetsOptions(options);
        } catch (error) {
            handleError(error, setErrors);
        }
    }, []);

    useEffect(() => {
        fetchAllAssets();
    }, [fetchAllAssets]);

    const handleChange = async (key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }));

        if (key === 'assets_id') {
            if (value) {
                const selectedAsset = allAssetsData.find(asset => asset.platform_id === value);
                if (selectedAsset) {
                    try {
                        const assetDetails = await getAssetByName(selectedAsset.name);
                        if (assetDetails && assetDetails.price !== undefined) {
                            setFormData((prev) => ({ ...prev, open_price: assetDetails.price.toString() }));
                        } else {
                            setFormData((prev) => ({ ...prev, open_price: '' }));
                        }
                    } catch (error) {
                        setFormData((prev) => ({ ...prev, open_price: '' }));
                        handleError(error, setErrors);
                    }
                } else {
                    setFormData((prev) => ({ ...prev, open_price: '' }));
                }
            } else {
                setFormData((prev) => ({ ...prev, open_price: '' }));
            }
        }

        if (touched[key] || errors[key]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                if (newErrors[key]) delete newErrors[key];
                return newErrors;
            });
        }
    };

    const fields = [
        { key: 'type', label: currentTranslation.type, type: 'dropdown', options: [{ value: 'BUY', label: 'BUY' }, { value: 'SELL', label: 'SELL' }], required: true },
        { key: 'assets_id', label: currentTranslation.asset, type: 'searchable-dropdown', options: assetsOptions, required: true },
        { key: 'leverage', label: currentTranslation.leverage, type: 'input', required: true, min: 1 },
        { key: 'open_price', label: currentTranslation.openPrice, type: 'input', required: true, min: 0, step: 'any' },
        { key: 'volume', label: currentTranslation.volume, type: 'input', required: true, min: 0, step: 'any' },
        { key: 'take_profit_price', label: currentTranslation.takeProfit, type: 'input', required: false, step: 'any' },
        { key: 'stop_loss_price', label: currentTranslation.stopLoss, type: 'input', required: false, step: 'any' },
        { key: 'margin', label: currentTranslation.margin, type: 'input', required: false, min: 0, step: 'any' },
    ];

    const inputFieldsConfig = fields.filter((field) => field.type === "input");
    const dropdownFieldsConfig = fields.filter((field) => field.type === "dropdown");
    const searchableDropdownFieldsConfig = fields.filter((field) => field.type === "searchable-dropdown");


    const handleFocus = (key) => {
        setErrors((prev) => {
            const newErrors = { ...prev };
            if (newErrors[key]) delete newErrors[key];
            return newErrors;
        });
    };

    const handleBlur = (key) => {
        setTouched((prev) => ({ ...prev, [key]: true }));
    };

    const handleSubmit = async () => {
        setErrors({});
        setLoading(true);

        try {
            const dataToSend = { ...formData };
            fields.forEach(f => {
                if (['leverage', 'open_price', 'volume', 'take_profit_price', 'stop_loss_price', 'margin'].includes(f.key)) {
                    dataToSend[f.key] = dataToSend[f.key] === '' ? null : parseFloat(dataToSend[f.key]);
                }
                if (f.key === 'assets_id' && dataToSend[f.key] === '') {
                    dataToSend[f.key] = null;
                }
            });

            await createOrder(leadId, dataToSend);
            onOrderCreated();
        } catch (err) {
            handleError(err.response?.data || err, setErrors);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    const modalClass = theme === 'dark' ? `${styles.modal} ${styles.darkModal}` : styles.modal;
    const filtersContainerClass = theme === 'dark' ? `${styles.filtersContainer} ${styles.darkFiltersContainer}` : styles.filtersContainer;
    const modalLabelClass = theme === 'dark' ? `${styles.modalLabel} ${styles.darkModalLabel}` : styles.modalLabel;
    const modalContentClass = theme === 'dark' ? `${styles.modalContent} ${styles.darkModalContent}` : styles.modalContent;
    const modalFooterClass = theme === 'dark' ? `${styles.modalFooter} ${styles.darkModalFooter}` : styles.modalFooter;
    const submitButtonClass = theme === 'dark' ? `${styles.submitButton} ${styles.darkSubmitButton}` : styles.submitButton;

    return (
        <div className={modalClass}>
            {errors.general && <Notification message={errors.general} type={"error"} />}

            <div className={filtersContainerClass} ref={modalRef}>
                <div className={modalLabelClass}>
                    <p>{currentTranslation.title}</p>
                    <div onClick={onClose}>
                        <CloseIcon className={theme === 'dark' ? styles.darkCloseIcon : ''} />
                    </div>
                </div>

                <div className={modalContentClass}>
                    {dropdownFieldsConfig.map((field) => (
                        <div
                            key={field.key}
                            className={styles.inputRow}
                            style={{ gridColumn: 'span 6' }}
                        >
                            <label className={theme === 'dark' ? styles.darkLabel : ''}>{field.label}:</label>
                            <DropdownSelector
                                filterName={field.key}
                                selectedValue={formData[field.key]}
                                handleFilterChange={handleChange}
                                options={field.options || []}
                                onFocus={() => handleFocus(field.key)}
                                onBlur={() => handleBlur(field.key)}
                                hasError={!!errors[field.key]}
                                errorMessage={errors[field.key]}
                                theme={theme}
                            />
                        </div>
                    ))}

                    {searchableDropdownFieldsConfig.map((field) => (
                        <div
                            key={field.key}
                            className={styles.inputRow}
                            style={{ gridColumn: 'span 6' }}
                        >
                            <SearchableDropdown
                                label={field.label}
                                options={field.options || []}
                                selectedValue={formData[field.key]}
                                onSelect={(value) => handleChange(field.key, value)}
                                onFocus={() => handleFocus(field.key)}
                                onBlur={() => handleBlur(field.key)}
                                hasError={!!errors[field.key]}
                                errorMessage={errors[field.key]}
                                isTouched={touched[field.key]}
                                theme={theme}
                            />
                        </div>
                    ))}

                    {inputFieldsConfig.map((field) => (
                        <div
                            key={field.key}
                            className={styles.inputRow}
                            style={{ gridColumn: 'span 6' }}
                        >
                            <label className={theme === 'dark' ? styles.darkLabel : ''}>{field.label}:</label>
                            <InputSelector
                                filterName={field.key}
                                selectedValue={formData[field.key]}
                                handleFilterChange={handleChange}
                                onFocus={() => handleFocus(field.key)}
                                onBlur={() => handleBlur(field.key)}
                                hasError={!!errors[field.key]}
                                errorMessage={errors[field.key]}
                                type={['leverage', 'open_price', 'volume', 'take_profit_price', 'stop_loss_price', 'margin'].includes(field.key) ? 'number' : 'text'}
                                isTouched={touched[field.key]}
                                theme={theme}
                            />
                        </div>
                    ))}
                </div>

                <div className={modalFooterClass}>
                    <div className={styles.confirmationWrapper}>
                        <button className={submitButtonClass} onClick={handleSubmit} disabled={loading}>
                            {loading ? currentTranslation.creating : currentTranslation.confirm}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};